import { ref, markRaw } from 'vue';

export class FilterItemsContext {
  constructor(duckDBService) {
    markRaw(this);
    this.duckDBService = markRaw(duckDBService);
    this.filterQueries = markRaw({});     // Store the actual queries
    this.filters = markRaw({});           // Visual metadata
    this.filtersLastUpdated = ref(new Date());
    this.showOpenItemsOnly = false;       // Track open items only filter
  }

  // Base items selection honoring the showOpenItemsOnly setting
  getBaseItemsQuery() {
    if (this.showOpenItemsOnly) {
      // Only include items where status is NOT terminal (open items only)
      return `
        SELECT DISTINCT i.SubItemId
        FROM items i
        JOIN status_templates st ON i.StatusId = st.Id
        WHERE st.IsTerminal = FALSE
      `;
    }
    return `SELECT DISTINCT SubItemId FROM items`;
  }

  async initializeFilterContext(options = {}) {
    const conn = this.duckDBService.conn;
    const { showOpenItemsOnly = false } = options;

    this.showOpenItemsOnly = showOpenItemsOnly;
    const baseItemsQuery = this.getBaseItemsQuery();

    await conn.query(`
      DROP VIEW  IF EXISTS filteredItems;
      DROP TABLE IF EXISTS filter_context;

      CREATE TEMPORARY TABLE filter_context(SubItemId VARCHAR);
      CREATE UNIQUE INDEX filter_context_idx ON filter_context(SubItemId);

      INSERT INTO filter_context
      ${baseItemsQuery};

      CREATE VIEW filteredItems AS
      SELECT
          i.ClientId AS clientId,
          i.Id AS id,
          i.LocationId AS locationId,
          i.SubItemId AS subItemId,
          i.StatusId AS statusId,
          i.StatusCreatedDate AS statusCreatedDate,
          i.AssigneeTeamId AS assigneeTeamId,
          i.AssigneeUserIds AS assigneeUserIds,
          i.AssigneeCreatedDate AS assigneeCreatedDate,
          i.SubItemTemplateId AS subItemTemplateId,
          i.ControlledByTeamId AS controlledByTeamId,
          i.CreatedByTeamId AS createdByTeamId,
          i.CreatedByUserId AS createdByUserId,
          i.ClosedDate AS closedDate,
          i.CreatedDate AS createdDate,
          i.ItemTemplateId AS itemTemplateId,
          i.ProfileId AS profileId,
          i."Type" AS type
      FROM items i
      INNER JOIN filter_context fc USING(SubItemId);
    `);

    this.filters = markRaw({});
    this.filterQueries = markRaw({});
    this.filtersLastUpdated.value = new Date();
  }

  // New method: Update filter directly from SQL
  async updateFilterWithQuery(visualId, query) {
    const conn = this.duckDBService.conn;
    
    try {
      // Register query as a view for this filter
      await conn.query(`
        DROP VIEW IF EXISTS filter_view_${visualId};
        CREATE TEMPORARY VIEW filter_view_${visualId} AS (
          SELECT DISTINCT SubItemId FROM (${query})
        );
      `);
      
      // Store the query reference
      this.filterQueries[visualId] = query;

      // Get count for metadata. conn.query swallows SQL errors and returns an
      // empty result, so a missing row means the view was never created —
      // bail out loudly instead of registering a broken filter
      const countResult = await conn.query(`SELECT COUNT(*) as count FROM filter_view_${visualId}`);
      const countRows = countResult.toArray();
      if (countRows.length === 0) {
        delete this.filterQueries[visualId];
        console.error(`Filter view for "${visualId}" could not be created; filter not applied. Query:\n${query}`);
        return;
      }
      this.filters[visualId] = countRows[0].count;
      
      // Apply the combined filters
      await this.applyFiltersWithSQL();
      
    } catch (error) {
      console.error('Error updating filter with query:', error);
      // Clean up if needed
    }
  }

  // Apply all current filters using SQL intersection
  async applyFiltersWithSQL() {
    const conn = this.duckDBService.conn;
    const filterIds = Object.keys(this.filters);
    
    if (filterIds.length === 0) {
      await this.resetFilters();
      return;
    }
    
    try {
      // Use SQL INTERSECT to combine all filters.
      // Filter views may select from the raw items table, so always intersect
      // with the base set to keep the showOpenItemsOnly restriction applied.
      const selects = filterIds.map(id => `SELECT SubItemId FROM filter_view_${id}`);
      if (this.showOpenItemsOnly) {
        selects.push(this.getBaseItemsQuery());
      }
      const intersectionQuery = selects.join(' INTERSECT ');

      await conn.query(`
        DELETE FROM filter_context;
        INSERT INTO filter_context
        ${intersectionQuery};
      `);

      this.filtersLastUpdated.value = new Date();
    } catch (error) {
      console.error('Error applying filters with SQL:', error);
      // Recover from error
      await this.resetFilters();
    }
  }

  // Legacy support for ID-based filtering
  async updateFilter(visualId, filteredIds) {
    const conn = this.duckDBService.conn;
    
    // Store for tracking
    this.filters[visualId] = filteredIds.length;
    
    if (filteredIds.length === 0) {
      // Empty filter case
      await conn.query(`
        DROP VIEW IF EXISTS filter_view_${visualId};
        CREATE TEMPORARY VIEW filter_view_${visualId} AS 
        SELECT CAST(NULL AS VARCHAR) AS SubItemId WHERE 1=0;
      `);
    } else {
      // Create a temporary table and view
      const tempTable = `temp_filter_${visualId}`;
      
      await conn.query(`DROP TABLE IF EXISTS ${tempTable}`);
      await conn.query(`CREATE TEMPORARY TABLE ${tempTable}(SubItemId VARCHAR)`);
      
      // Insert data in batches
      const batchSize = 200000;
      for (let i = 0; i < filteredIds.length; i += batchSize) {
        const chunk = filteredIds.slice(i, i + batchSize)
          .map(v => `('${v.replace(/'/g, "''")}')`).join(',');
        
        if (chunk) {
          await conn.query(`INSERT INTO ${tempTable} VALUES ${chunk}`);
        }
      }
      
      // Create a view from the table. The table must outlive the view: views
      // bind lazily, so dropping it here would break every later read of the
      // view. It is dropped when the filter is removed or replaced.
      await conn.query(`
        DROP VIEW IF EXISTS filter_view_${visualId};
        CREATE TEMPORARY VIEW filter_view_${visualId} AS
        SELECT DISTINCT SubItemId FROM ${tempTable};
      `);
    }
    
    // Apply combined filters
    await this.applyFiltersWithSQL();
  }

  async removeFilter(visualId) {
    // Check with `in`: a registered filter matching zero rows has count 0,
    // which is falsy but must still be removable
    if (!(visualId in this.filters)) return;
    
    const conn = this.duckDBService.conn;
    
    // Remove filter view and its backing table (if the legacy path created one)
    await conn.query(`DROP VIEW IF EXISTS filter_view_${visualId}`);
    await conn.query(`DROP TABLE IF EXISTS temp_filter_${visualId}`);

    // Remove from tracking
    delete this.filters[visualId];
    delete this.filterQueries[visualId];
    
    if (Object.keys(this.filters).length === 0) {
      await this.resetFilters();
    } else {
      await this.applyFiltersWithSQL();
    }
  }

  async resetFilters() {
    const conn = this.duckDBService.conn;

    // Use the same base query as initializeFilterContext based on showOpenItemsOnly
    const baseItemsQuery = this.getBaseItemsQuery();

    await conn.query(`
      DELETE FROM filter_context;
      INSERT INTO filter_context
      ${baseItemsQuery};
    `);

    this.filters = markRaw({});
    this.filterQueries = markRaw({});
    this.filtersLastUpdated.value = new Date();
  }

  async getFilteredIds() {
    const conn = this.duckDBService.conn;
    try {
      const res = await conn.query('SELECT SubItemId FROM filter_context');
      if (res?.getColumn) return res.getColumn('SubItemId').toArray();
      if (Array.isArray(res)) return res.map(r => r.SubItemId);
      if (res?.data) return res.data.map(r => r.SubItemId);
      return [];
    } catch (error) {
      console.error('Error getting filtered IDs:', error);
      return [];
    }
  }

  // Keep the executeFilteredQuery method as is
  async executeFilteredQuery(sql) {
    const conn = this.duckDBService.conn;
    const ts = this.filtersLastUpdated.value.getTime();
    try {
      return await conn.query(`
        SELECT q.*
        FROM (${sql}) AS q
        INNER JOIN filter_context fc USING(SubItemId)
        /* ${ts} */
      `);
    } catch (error) {
      console.error('Error executing filtered query:', error);
      throw error; 
    }
  }

  getFilterDebugInfo() {
    return {
      activeFilters: Object.keys(this.filters).length,
      filtersByVisual: Object.entries(this.filters).map(([vid, count]) => ({
        visualId: vid,
        matchCount: count,
        hasQuery: !!this.filterQueries[vid]
      })),
      lastUpdated: this.filtersLastUpdated.value
    };
  }
}